<template>
    <ContentComp
        :fill-content="true"
        class="p-0"
        :load-state="LoadState"
    >
        <WizardComp
            :wizardDefinition="wizardDefinition"
            :showPrevNext="false"
            :showHeader="false"
            ref="wizard"
            class="h-100"
            :useGivenSpace="true"
            @tabShown="onTabShown"
        >
            <template #wizardPage-selection>
                <p v-html="$tm('eventwizard.createneweventinfo', '', true)"></p>

                <div class="d-flex justify-content-center gap-5 flex-row flex-nowrap">
                    <ButtonComp
                        class="btn-lg bg-white opacity-75 opacity-hover-100 border"
                        buttonType="Custom"
                        @click="showWizardPage(1)"
                        :style="{
                            'background-image': `url(https://cdn-assets.productfruits.com/fefd9ef6-0392-4624-9df9-269985775404)`,
                            'background-size': '95% 95%',
                            'background-position': 'center',
                            width: '200px',
                        }"
                    ></ButtonComp>

                    <ButtonComp
                        class="btn-lg"
                        buttonType="Primary"
                        icon="fa-copy"
                        @click="showWizardPage(2)"
                        >{{ $tm('eventwizard.copyexisting') }}</ButtonComp
                    >

                    <ButtonComp
                        class="btn-lg"
                        buttonType="Primary"
                        icon="fa-plus"
                        @click="showWizardPage(3)"
                        >{{ $tm('event.createnewevent') }}</ButtonComp
                    >
                </div>
            </template>

            <template #wizardPage-externaldatasource>
                <ExternalEventSourcesView
                    v-model="model"
                    v-if="currentWizardPageIndex == 1"
                />
            </template>
            <template #wizardPage-copyexisting>
                <EventinfoCopyExistingView
                    v-model="model"
                    v-if="currentWizardPageIndex == 2"
                    :previous-events="previousEvents"
                />
            </template>

            <template #wizardPage-createnew>
                <div v-if="currentWizardPageIndex == 3">
                    <EventinfoDetailsView
                        v-if="model.EventInfo"
                        v-model="model.EventInfo"
                        :eventslist="null"
                    />

                    <DisplaySeparator
                        class="py-3"
                        v-model="categorySeparator"
                    />

                    <CreateCategoryTemplateView
                        class="p-0"
                        :sport-id="model.EventInfo!.SportID"
                        :previous-events="null"
                        v-model="model.Categories"
                    />
                </div>
            </template>
        </WizardComp>
    </ContentComp>
</template>
<script lang="ts" setup>
import { computed, onMounted, provide, ref, watch, type Ref } from 'vue';
import { DisplaySeparator, WizardComp } from '@/components';
import { CreateEventRequest, EventinfoBase, ExternalEventDatasourceResponse } from '@/objects/generated';
import ContentComp from '@/components/layout/ContentComp.vue';
import ExternalEventSourcesView from './ExternalEventSourcesView.vue';
import EventinfoDetailsView from './EventinfoDetailsView.vue';
import { EventDao } from '@/dao';
import ButtonComp from '@/components/Basic/ButtonComp.vue';
import { useHelper, useLoaders, useTaskLoader } from '@/composeables';
import CreateCategoryTemplateView from '../EventDashboard/CreateCategoryTemplateView.vue';
import EventinfoCopyExistingView from './EventinfoCopyExistingView.vue';
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import type { IAlertMessage } from '@/objects';
import { useUserStoreRef } from '@/stores';

const model = defineModel<CreateEventRequest>({ required: true });

const wizard = ref<InstanceType<typeof WizardComp> | null>(null);

const wizardDefinition = new WizardContainer();
wizardDefinition.WizardPages = [
    new WizardPageDefinition('selection', ''),
    new WizardPageDefinition('externaldatasource', ''),
    new WizardPageDefinition('copyexisting', ''),
    new WizardPageDefinition('createnew', ''),
];

const { LoadState, AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['FEDERATIONS']);

const { LoadState: PreviousEventsLoadState, AddLoader: AddPreviousEventsLoader } = useTaskLoader();

// The ID of the source event we want to copy the settings from
const copyEventSourceId = ref<number | null>(null);

// The external source for the event
const externalSource = ref<ExternalEventDatasourceResponse | null>(null);

// If we want to create a new event from scratch
const isCreatingNewEvent = ref<boolean>(false);

// helper property for the category separator
const categorySeparator = ref<string>('categories.categories');

const { user } = useUserStoreRef();

const emits = defineEmits<{
    (e: 'datesourceselected'): void;
}>();

// Just emit that we have made a selection for a new event type
watch([externalSource, isCreatingNewEvent, copyEventSourceId], () => {
    useHelper().setFocus();
    emits('datesourceselected');
});

/**
 * Whenever we swich a tab we want to set the focus to the first imput
 */
const onTabShown = () => {
    useHelper().setFocus();
};

const previousEvents = ref<EventinfoBase[] | null>(null);

const currentWizardPageIndex = ref<number>(0);

/**
 * Helper function to show a specific wizard page
 * @param index
 */
const showWizardPage = (index: number) => {
    currentWizardPageIndex.value = index;
    wizard.value?.goto(wizardDefinition.WizardPages[index]);
};

onMounted(async () => {
    await AddPreviousEventsLoader(async () => {
        if (user.value?.IsSuperAdmin) {
            // superadmins will have to search for events, because they can have a lot of events and loading them all would be bad for performance
            previousEvents.value = null;
        } else {
            /**
             * We find the 5 last events of the organizer.
             */
            previousEvents.value =
                ((await EventDao.GetPreviousEventsForOrganizer(24, null)) ?? [])
                    ?.sort((a, b) => b.Date.getTime() - a.Date.getTime())
                    .slice(0, 5) ?? null;
        }
    }, '');
});

/**
 * This is the alert message we will show when the event is created
 * @type {IAlertMessage | null}
 */
const fieldAlertMessages = ref<Map<string, IAlertMessage>>(new Map<string, IAlertMessage>());
provide('QuickCreateAlertMessage', fieldAlertMessages);

defineExpose({
    /**
     * This will return the current state of the wizard
     */
    ErrorMessages: computed(() => [...fieldAlertMessages.value.values()]),
});
</script>
